import { useEffect, useRef, useState } from "react";
import type { GLViewer } from "3dmol";
import { Loader2 } from "lucide-react";
import { useAssistantContext } from "~/components/assistant/AssistantContext";
import { cn } from "~/lib/utils";

const STRUCTURE_EXTENSIONS = new Set(["pdb", "cif", "mmcif"]);

const MAX_STRUCTURE_FILE_BYTES = 20 * 1024 * 1024; // 20 MB

export function isProteinStructureFile(fileName: string): boolean {
  const ext = fileName.split(".").pop()?.toLowerCase() ?? "";
  return STRUCTURE_EXTENSIONS.has(ext);
}

function getFormatFromFileName(fileName: string): string {
  const ext = fileName.split(".").pop()?.toLowerCase() ?? "";
  if (ext === "cif" || ext === "mmcif") return "cif";
  return "pdb";
}

interface ProteinStructureViewerProps {
  artifactId: string;
  fileName: string;
}

export function ProteinStructureViewer({ artifactId, fileName }: ProteinStructureViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<GLViewer | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { api, conversationId } = useAssistantContext();

  useEffect(() => {
    if (!containerRef.current || !conversationId || !artifactId) return;

    let cancelled = false;
    let resizeObserver: ResizeObserver | null = null;

    const loadStructure = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const downloadUrl = await api.getArtifactDownloadUrl(conversationId, artifactId);
        const response = await fetch(downloadUrl);
        if (!response.ok) {
          throw new Error(`Server returned ${response.status}`);
        }

        const contentLength = response.headers.get("Content-Length");
        if (contentLength && Number(contentLength) > MAX_STRUCTURE_FILE_BYTES) {
          throw new Error("File is too large to render inline. Please download it instead.");
        }

        const structureData = await response.text();

        if (cancelled) return;

        const $3Dmol = await import("3dmol");

        if (cancelled || !containerRef.current) return;

        if (viewerRef.current) {
          viewerRef.current.clear();
        }

        // Hardcoded to white — update if dark mode support is added
        const viewer = $3Dmol.createViewer(containerRef.current, {
          backgroundColor: "white",
        });
        viewerRef.current = viewer;

        const format = getFormatFromFileName(fileName);
        viewer.addModel(structureData, format);
        viewer.setStyle({}, { cartoon: { color: "spectrum" } });
        viewer.zoomTo();
        viewer.render();

        resizeObserver = new ResizeObserver(() => {
          viewer.resize();
          viewer.render();
        });
        resizeObserver.observe(containerRef.current);
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load protein structure:", err);
          setError(err instanceof Error ? err.message : "Unknown error");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadStructure();

    return () => {
      cancelled = true;
      resizeObserver?.disconnect();
      if (viewerRef.current) {
        viewerRef.current.clear();
        viewerRef.current = null;
      }
    };
  }, [api, conversationId, artifactId, fileName]);

  if (error) {
    return (
      <div className="flex items-center justify-center h-96 rounded-md border border-gray-200 bg-gray-50 text-sm text-gray-500">
        {error}
      </div>
    );
  }

  return (
    <div className="relative w-full h-[450px]">
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
          <div className="text-center">
            <Loader2 className="size-8 text-gray-400 animate-spin mx-auto mb-2" />
            <p className="text-sm text-gray-500">Loading structure...</p>
          </div>
        </div>
      )}
      <div
        ref={containerRef}
        className={cn("w-full h-full rounded-md", isLoading && "invisible")}
      />
    </div>
  );
}
